import { openToast } from '@component/toast';
import ISSUE_NAME from '@constant/issue-name';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { BOOL } from '@tencent/wemeet-websdk-next';
import { handleDeviceErr } from '@utils/helper/media';
import wemeetClient from '@utils/wemeetClient';
import { Translation } from 'react-i18next';

// 老版本有这个
const deviceTransform = (item: MediaDeviceInfo) => ({
  label: item.label,
  deviceId: item.deviceId,
  key: `${item.kind}-${item.deviceId}`,
});

export const getAudioDevices = createAsyncThunk('meeting/audioDevice', async () => {
  if (window.navigator?.mediaDevices) {
    try {
      const mediaStream = await window.navigator.mediaDevices.getUserMedia({ audio: true });
      // 请求麦克风授权后，应该释放掉麦克风资源, 老逻辑我也不知道为啥
      if (mediaStream && typeof mediaStream.getTracks === 'function') {
        const mediaTracks = mediaStream.getTracks();
        if (mediaTracks.length > 0) {
          mediaTracks[0].stop();
        }
      }

      const microphones = await wemeetClient.mediaCtrl.getMicrophones();
      // const speakers = await wemeetClient.mediaCtrl.getSpeakers();
      const speakers: any[] = []; // 老版本注释了不知道为什么
      const invalidDeviceId = ['communications', 'default'];
      return {
        // 过滤虚拟设备,以及默认设备 chrome浏览器获取到的设备列表中，会多出一个deviceId为default的设备
        // 与sdk人沟通过，不建议使用这项，会有明显的bug
        microphones: microphones
          .filter(
            (item: MediaDeviceInfo) =>
              !invalidDeviceId.includes(item?.deviceId) && !(item?.label ?? '').includes('(Virtual)'),
          )
          .map(deviceTransform),
        speakers: speakers
          .filter((item: MediaDeviceInfo) => !invalidDeviceId.includes(item?.deviceId))
          .map(deviceTransform),
        allowAudio: true,
      };
    } catch (err) {
      openToast(<Translation>{(t) => t('无录音设备')}</Translation>);
    }
  } else {
    openToast(<Translation>{(t) => t('您的浏览器不支持访问麦克风设备')}</Translation>);
  }
});

const getDefaultCameraId = (mediaTracks: any) => {
  const mediaTracksSettings = mediaTracks[0].getSettings();
  if (mediaTracksSettings?.deviceId) {
    const invalidDeviceId = ['communications', 'default'];
    return invalidDeviceId.indexOf(mediaTracksSettings.deviceId) >= 0 ? '' : mediaTracksSettings.deviceId;
  }
};

export const getCameraDevice = createAsyncThunk('meeting/cameraDevice', async () => {
  let defaultCameraId;
  if (window.navigator?.mediaDevices) {
    const mediaStream = await window.navigator.mediaDevices.getUserMedia({ video: true });
    // 请求摄像头授权后，应该释放掉摄像头资源 老逻辑我也不知道为啥
    if (mediaStream && typeof mediaStream.getTracks === 'function') {
      const mediaTracks = mediaStream.getTracks();
      if (mediaTracks.length > 0) {
        defaultCameraId = getDefaultCameraId(mediaTracks);
        mediaTracks[0].stop();
      }
    }

    const cameras = await wemeetClient.mediaCtrl.getCameras();
    const invalidDeviceId = ['communications', 'default'];
    // 过滤虚拟设备,以及默认设备 chrome浏览器获取到的设备列表中，会多出一个deviceId为default的设备
    // 与sdk人沟通过，不建议使用这项，会有明显的bug
    const cameraList = cameras
      .filter((item: MediaDeviceInfo) => !invalidDeviceId.includes(item?.deviceId))
      .map(deviceTransform);

    return {
      cameraList,
      defaultCameraId,
      allowCamera: true,
    };
  }
});

export const unMuteAudio = createAsyncThunk('meeting/unMuteAudio', async () => {
  try {
    await wemeetClient.mediaCtrl.unMuteAudio({
      needRefreshToken: BOOL.true,
      requestUpStreamToken: '',
    });

    return true;
  } catch (error) {
    // 开麦错误
    handleDeviceErr('microphone', error);
    console.log(ISSUE_NAME.UNMUTE_AUDIO_ERROR, ':', error);
    window?.aegis?.infoAll(`${ISSUE_NAME.UNMUTE_AUDIO_ERROR} : ${error}`);
    return false;
  }
});

export const muteAudio = createAsyncThunk('meeting/muteAudio', async () => {
  try {
    await wemeetClient.mediaCtrl.muteAudio({
      needRefreshToken: BOOL.false,
      requestUpStreamToken: '',
    });
  } catch (error) {
    handleDeviceErr('microphone', error);
    console.log(ISSUE_NAME.MUTE_AUDIO_ERROR, ':', error);
    window?.aegis?.infoAll(`${ISSUE_NAME.MUTE_AUDIO_ERROR} : ${error}`);
  }
});

export const unMuteVideo = createAsyncThunk('meeting/unMuteVideo', async () => {
  try {
    await wemeetClient.mediaCtrl.unMuteVideo({
      needRefreshToken: BOOL.false,
      requestUpStreamToken: '',
    });
    return true;
  } catch (error) {
    handleDeviceErr('camera', error);
    console.log(ISSUE_NAME.UNMUTE_VIDEO_ERROR, ':', error);
    window?.aegis?.infoAll(`${ISSUE_NAME.UNMUTE_VIDEO_ERROR} : ${error}`);
    return false;
  }
});

export const muteVideo = createAsyncThunk('meeting/muteVideo', async () => {
  try {
    await wemeetClient.mediaCtrl.muteVideo({
      needRefreshToken: BOOL.false,
      requestUpStreamToken: '',
    });
    return false;
  } catch (error) {
    handleDeviceErr('camera', error);
    console.log(ISSUE_NAME.MUTE_VIDEO_ERROR, ':', error);
    window?.aegis?.infoAll(`${ISSUE_NAME.MUTE_VIDEO_ERROR} : ${error}`);
    return true;
  }
});

export const switchMicPhone = createAsyncThunk('meeting/switchMicPhone', async (device: MediaDeviceInfo) =>
  wemeetClient.mediaCtrl
    .switchMicPhone(device)
    .then((deviceId) => {
      openToast(<Translation>{(t) => t('切换成功')}</Translation>);
      return deviceId;
    })
    .catch(() => {
      openToast(<Translation>{(t) => t('切换失败')}</Translation>);
    }),
);

/** @description 切换到默认麦克风 */
export const switchDefaultMic = createAsyncThunk('meeting/switchDefaultMicPhone', async () => {
  // 变更回系统默认，分两步走
  // 如果是chrome、Opera之类的浏览器，可以根据设备的deviceid等于default获取
  // 如果不支持default字段，创建音频流获取默认设备
  try {
    const microphones = await wemeetClient.mediaCtrl.getMicrophones();
    if (!Array.isArray(microphones)) {
      throw '切换失败';
    }

    // 找到默认麦克风的groupId
    let defaultMicGroupId = '';
    const invalidDeviceId = ['communications', 'default'];
    const micDeviceArr = microphones.filter((item: any) => {
      const { kind, deviceId, groupId } = item;
      if (kind !== 'audioinput') {
        return false;
      }
      if (deviceId === 'default') {
        defaultMicGroupId = groupId;
      }

      return deviceId && invalidDeviceId.indexOf(deviceId) < 0;
    });

    if (defaultMicGroupId) {
      await wemeetClient.mediaCtrl.switchMicPhone(
        micDeviceArr.reduce(
          (total: any, item: any) => {
            if (item.groupId === defaultMicGroupId) {
              total.deviceId = item.deviceId;
            }
            return total;
          },
          { deviceId: null },
        ),
      );

      openToast(<Translation>{(t) => t('切换成功')}</Translation>);
      return '';
    }
    // 并不是chrome或者Opera浏览器，不支持显示系统默认设备
    // 使用创建音频媒体流的方式解决
    if (window.navigator?.mediaDevices) {
      const mediaStream = await window.navigator.mediaDevices.getUserMedia({ audio: true });
      if (mediaStream && typeof mediaStream.getTracks === 'function') {
        const mediaTracks = mediaStream.getTracks();
        if (mediaTracks.length <= 0) {
          throw '切换失败';
        }
        const mediaTracksSettings = mediaTracks[0].getSettings();
        mediaTracks[0].stop();
        // @ts-ignore
        await wemeetClient.mediaCtrl.switchMicPhone({
          deviceId: mediaTracksSettings ? mediaTracksSettings?.deviceId ?? '' : '',
        });

        openToast(<Translation>{(t) => t('切换成功')}</Translation>);
        return '';
      }
    }
    throw '切换失败';
  } catch (error) {
    openToast(<Translation>{(t) => t('切换失败')}</Translation>);
  }
});

export const switchCamera = createAsyncThunk('meeting/switchCamera', async (device: MediaDeviceInfo) =>
  wemeetClient.mediaCtrl
    .switchCamera(device)
    .then((deviceId) => {
      openToast(<Translation>{(t) => t('切换成功')}</Translation>);
      return deviceId;
    })
    .catch(() => {
      openToast(<Translation>{(t) => t('切换失败')}</Translation>);
    }),
);
